/**
 * Goal Mode on a TABLE: the template holds exactly one body row, and when the
 * note is filled staff pick goals ("Add Goal") and that row is repeated once per
 * picked goal, each copy resolved with its own goal's tokens — so `@GoalName` and
 * `@GoalBaseline` in the same row stay paired. Each goal row carries `_goalId`.
 *
 * Per-row table layout (`cellTypes`, `cellOptions`, `cellStyles`, `splitCells`, `cellRequired`,
 * `cellMinSelections`, `cellReadOnly` keyed `${row}_${colId}[_left|_right]`, `rowStyles` keyed by row, `mergedCells`
 * keyed `${startRow}_${startCol}`) is copied from row 0 onto every goal row, so
 * the stored note renders correctly in every table renderer (builder, previews,
 * PDF converters) without any of them knowing about Goal Mode.
 */

/**
 * Clinical-note `formDefault` key with the client's goals for Goal Mode fields:
 * `{ id, type, status, ...goal tokens, targets: [{ id, status, ...target tokens }] }`
 * per goal. Mirrors `GOAL_MODE_GOALS_KEY`
 * in `@therapylake/package/form-builder/constants` (the client cannot import it).
 */
export const GOAL_MODE_GOALS_KEY = 'GOAL_MODE_GOALS';

const ROW_KEYED_MAPS = ['cellTypes', 'cellOptions', 'cellStyles', 'splitCells', 'cellRequired', 'cellMinSelections', 'cellReadOnly'];
const MENTION_REGEX = /<span data-id="([^"]+)"[^>]*>.*?<\/span>/g;

const keyRow = (key) => parseInt(String(key).split('_')[0], 10);

const filterEntries = (map, keep) => (map ? Object.fromEntries(Object.entries(map).filter(keep)) : map);

/** Template side: drop every body row but the first, with its layout entries. */
export const keepFirstRowOnly = (table = {}, body = [], makeRow) => {
    const next = { ...table };
    ROW_KEYED_MAPS.forEach((m) => {
        next[m] = filterEntries(table[m], ([key]) => keyRow(key) < 1);
    });
    next.rowStyles = filterEntries(table.rowStyles, ([key]) => Number(key) < 1);
    // A merge reaching past row 0 would reference rows that no longer exist.
    next.mergedCells = filterEntries(table.mergedCells, ([, m]) => m.startRow + m.rowSpan <= 1);
    return { table: next, body: [body[0] || makeRow()] };
};

/** Fill side: copy row 0's layout entries onto rows 1..rowCount-1. */
export const repeatFirstRowLayout = (table = {}, rowCount) => {
    const next = { ...table };
    ROW_KEYED_MAPS.forEach((m) => {
        if (!table[m]) return;
        const map = filterEntries(table[m], ([key]) => keyRow(key) < 1);
        Object.entries(map).forEach(([key, val]) => {
            if (keyRow(key) !== 0) return;
            const rest = String(key).slice(2);
            for (let r = 1; r < rowCount; r++) map[`${r}_${rest}`] = val;
        });
        next[m] = map;
    });
    if (table.rowStyles?.[0]) {
        next.rowStyles = { ...filterEntries(table.rowStyles, ([key]) => Number(key) < 1) };
        for (let r = 1; r < rowCount; r++) next.rowStyles[r] = table.rowStyles[0];
    }
    if (table.mergedCells) {
        const merges = filterEntries(table.mergedCells, ([, m]) => m.startRow + m.rowSpan <= 1);
        Object.values(merges).forEach((m) => {
            if (m.startRow !== 0) return;
            for (let r = 1; r < rowCount; r++) merges[`${r}_${m.startCol}`] = { ...m, startRow: r };
        });
        next.mergedCells = merges;
    }
    return next;
};

/**
 * Replace the mentions in one table cell. Table filler cells are plain inputs,
 * so a resolved cell is stripped of its remaining markup and entity-decoded.
 * Returns the input unchanged when nothing resolved.
 */
export const resolveTableCell = (cell, defaults) => {
    if (typeof cell !== 'string') return cell;
    const replaced = cell.replace(MENTION_REGEX, (match, dataId) => {
        const val = defaults[dataId];
        if (val === null) return '';
        return val !== undefined ? val : match;
    });
    if (replaced === cell) return cell;
    const txt = document.createElement('textarea');
    txt.innerHTML = replaced.replace(/<[^>]*>/g, '');
    return txt.value;
};

/** One resolved copy of the template row per goal (`defaults.id` is the goal id). */
export const expandGoalRows = (templateRow, goalDefaultsList) =>
    goalDefaultsList.map((defaults) => {
        const row = {};
        Object.entries(templateRow).forEach(([colId, cell]) => {
            row[colId] = colId === '_rowId' ? cell : resolveTableCell(cell, defaults);
        });
        row._rowId = `${templateRow._rowId || '_row'}_goal_${defaults.id}`;
        row._goalId = defaults.id;
        return row;
    });

/**
 * Target tokens (`CLINICAL_TARGET` in `@therapylake/package/form-builder/constants`;
 * the client cannot import it). Mirrored here so a Target Mode row without a target
 * can blank them.
 */
export const TARGET_TOKEN_KEYS = [
    'TARGET_NAME',
    'TARGET_STATUS',
    'TARGET_OBJECTIVE',
    'TARGET_PROGRESS',
    'TARGET_CREATED_DATE',
    'TARGET_UPDATED_DATE',
    'TARGET_IS_MAINTENANCE',
];
// `null` resolves a token to '' (see `resolveTableCell`).
const BLANK_TARGET_TOKENS = Object.fromEntries(TARGET_TOKEN_KEYS.map((key) => [key, null]));

/**
 * Target Mode: one resolved copy of the template row per picked target, with its
 * goal's tokens and its own (`defaults.goalId` / `defaults.targetId`). Each row
 * carries `_goalId` and `_targetId`.
 *
 * Without `defaults.targetId` (a picked goal with no targets) the row is the
 * goal's alone: goal tokens filled, target tokens blank, no `_targetId` — the same
 * row id a plain Goal Mode row gets.
 */
export const expandTargetRows = (templateRow, targetDefaultsList) =>
    targetDefaultsList.map((defaults) => {
        const [row] = expandGoalRows(templateRow, [{ ...BLANK_TARGET_TOKENS, ...defaults, id: defaults.goalId }]);
        if (!defaults.targetId) return row;
        row._rowId = `${templateRow._rowId || '_row'}_goal_${defaults.goalId}_target_${defaults.targetId}`;
        row._targetId = defaults.targetId;
        return row;
    });

/**
 * Target Mode rows to build from a picker save: `{ goal, target }` per picked
 * target of the picked goals, in goal order, then the goal's target order. A
 * picked goal none of whose targets are picked (a goal with no targets) still
 * gets one row, `{ goal, target: null }`: staff picked it, so it must not vanish.
 */
export const targetModeRowSpecs = (goals = [], pickedGoalIds = [], targetIds = []) =>
    goals
        .filter((goal) => pickedGoalIds.includes(goal.id))
        .flatMap((goal) => {
            const targets = (goal.targets || []).filter((target) => targetIds.includes(target.id));
            return targets.length ? targets.map((target) => ({ goal, target })) : [{ goal, target: null }];
        });

const ANY_MENTION_REGEX = /<span[^>]*\bdata-id="([^"]+)"[^>]*>.*?<\/span>(?:&nbsp;| )?/g;

/**
 * Remove the mentions whose key is in `keys` from a field value — a rich-text /
 * input string, or a table body (rows of string or split `{ left, right }`
 * cells). Used when Goal Mode is switched off, so the field is not left holding
 * goal tokens that no longer resolve.
 */
export const stripMentions = (value, keys) => {
    if (!keys?.size) return value;
    const stripString = (str) =>
        typeof str === 'string' ? str.replace(ANY_MENTION_REGEX, (match, dataId) => (keys.has(dataId) ? '' : match)) : str;

    if (typeof value === 'string') return stripString(value);
    if (!Array.isArray(value)) return value;
    return value.map((row) => {
        if (!row || typeof row !== 'object') return row;
        const next = {};
        Object.entries(row).forEach(([colId, cell]) => {
            if (colId === '_rowId') next[colId] = cell;
            else if (cell && typeof cell === 'object' && !Array.isArray(cell) && ('left' in cell || 'right' in cell)) {
                next[colId] = { ...cell, left: stripString(cell.left), right: stripString(cell.right) };
            } else next[colId] = stripString(cell);
        });
        return next;
    });
};
